import React, { useEffect, useRef, useState } from "react";
import {
  Card,
  CardBody,
  Row,
  Col,
  Form,
  FormControl,
  Button,
  Dropdown,
  Nav,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import "./style.css";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { API_URL } from "../../../services/apiConfig";
import axiosInstance from "../../../services/axios.instance";

// IMPORTANT: make sure API_URL is imported from your constants/config file
// e.g. import API_URL from '../../config/api';
// and that you have a function/utility to call APIs (fetch/axios). This file uses axiosInstance.

interface DriverResponseItem {
  username: string;
  customerId: string;
  phone: string;
  firstname: string | null;
  lastname: string | null;
  approvalStatus: string;
  createdAt: string;
  // ...other fields from response if needed
}

const VendorDriverApproval: React.FC = () => {
  const [searchCriteria, setSearchCriteria] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [selectedApprovalTab, setSelectedApprovalTab] = useState<
    "PENDING" | "APPROVED"
  >("PENDING");

  const [drivers, setDrivers] = useState<DriverResponseItem[]>([]);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const initialRender = useRef(true);

  const formatDate = (dateString: string) => {
    const d = new Date(dateString);
    return `${String(d.getDate()).padStart(2, "0")}-${String(
      d.getMonth() + 1
    ).padStart(2, "0")}-${d.getFullYear()}`;
  };

  const buildDriversPayload = (pageToUse: number) => {
    return {
      user_type: "DELIVERY_EXECUTIVE",
      status: selectedApprovalTab, // "PENDING" or "APPROVED"
      index: pageToUse,
      count: pageSize,
      name: searchCriteria === "name" ? searchQuery : "",
      phone: searchCriteria === "phone_number" ? searchQuery : "",
    };
  };

  // Call GET_DRIVERS using axiosInstance and ensure token is present per-request
  const handleGetDrivers = async (
    pageToUse: number = 0,
    isNewSearch = false
  ) => {
    setLoading(true);
    try {
      // If token not set globally, attach per request (fallback)
      const token = localStorage.getItem("authToken");
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const payload = buildDriversPayload(pageToUse);

      // Example uses query params like your SERVICE_LIST example
      const url = `${API_URL.GET_DRIVERS}?size=${pageSize}&page=${pageToUse}`;

      console.log("GET_DRIVERS url:", url);
      console.log("GET_DRIVERS payload:", payload);
      console.log("GET_DRIVERS headers:", headers);

      const response = await axiosInstance.post(url, payload, { headers });

      // response.data expected shape: { total_records, total_pages, current_page, customers_details: [...] }
      const data = response.data || {};
      const customers = data.customers_details || data.customers || [];
      const mapped: DriverResponseItem[] = (customers as any[]).map((c) => ({
        username: c.username,
        customerId: c.customerId,
        phone: c.phone || c.phone_number || c.username,
        firstname: c.firstname,
        lastname: c.lastname,
        approvalStatus: c.approvalStatus || c.status || "PENDING",
        createdAt: c.createdAt,
      }));

      setDrivers(mapped);
      setTotalPages(data.total_pages ?? 0);
      setTotalRecords(data.total_records ?? mapped.length);

      // if triggered by a new search, reset UI page params (if desired)
      if (isNewSearch) {
        setCurrentPage(0);
        // Update search params url if you maintain them
      }
    } catch (err: any) {
      // axios error inspection
      console.error(
        "GET_DRIVERS error:",
        err?.response?.status,
        err?.response?.data || err.message
      );
      setDrivers([]);
      setTotalPages(0);
      setTotalRecords(0);
    } finally {
      setLoading(false);
      if (initialRender.current) initialRender.current = false;
    }
  };

  // Fetch when tab, page, pageSize or search changes
  useEffect(() => {
    handleGetDrivers(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedApprovalTab, currentPage, pageSize]);

  // invoked when user clicks Search (reset to page 0)
  const handleSearch = () => {
    setCurrentPage(0);
    handleGetDrivers(0, true);
  };

  return (
    <div className="row vendor-driver-approval-page">
      <Card className="card-bg-afl text-start">
        <CardBody className="px-0 pt-1">
          <div className="col-md-12 px-2">
            <Form.Group
              as={Row}
              className="align-items-center justify-content-between"
            >
              <Col md="8">
                <Row>
                  <Col sm="3">
                    <Form.Select
                      className="select-invoice-bg-black-edit dashboard-poppins-font-style "
                      aria-label="Select search criteria"
                      value={searchCriteria}
                      onChange={(e) => setSearchCriteria(e.target.value)}
                    >
                      <option value="">Search by</option>
                      <option value="name">Name</option>
                      <option value="phone_number">Mobile</option>
                    </Form.Select>
                  </Col>

                  {searchCriteria && (
                    <>
                      <Col sm="5">
                        <FormControl
                          className="merchant-input-style-create-product "
                          aria-label="Text input with select"
                          placeholder={
                            searchCriteria === "phone_number"
                              ? "Enter mobile number"
                              : "Enter name"
                          }
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                        />
                      </Col>

                      <Col sm="2">
                        <Button
                          className="btn all_button_style text-black my-1"
                          onClick={handleSearch}
                        >
                          Search
                        </Button>
                      </Col>
                    </>
                  )}
                </Row>
              </Col>
            </Form.Group>
          </div>

          <div className="col-md-12">
            <div className="row pt-2">
              <div className="px-2">
                <Nav variant="pills" className="status-toggle-pills">
                  <Nav.Item>
                    <Nav.Link
                      eventKey="PENDING"
                      active={selectedApprovalTab === "PENDING"}
                      onClick={() => {
                        setSelectedApprovalTab("PENDING");
                        setCurrentPage(0);
                      }}
                      className="d-flex align-items-center gap-2"
                    >
                      <HourglassBottomIcon
                        style={{ color: "#ffc107", fontSize: "18px" }}
                      />
                      <span className="dashboard-poppins-font-style">
                        PENDING
                      </span>
                    </Nav.Link>
                  </Nav.Item>

                  <Nav.Item>
                    <Nav.Link
                      eventKey="APPROVED"
                      active={selectedApprovalTab === "APPROVED"}
                      onClick={() => {
                        setSelectedApprovalTab("APPROVED");
                        setCurrentPage(0);
                      }}
                      className="d-flex align-items-center gap-2"
                    >
                      <DoneAllIcon
                        style={{ color: "#2DC24E", fontSize: "18px" }}
                      />
                      <span className="dashboard-poppins-font-style">
                        APPROVED
                      </span>
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
              </div>

              <div className="col-12 dashboard-table-div-vendor">
                <table className="table table-hover dashboard-table-vendor table">
                  <thead>
                    <tr>
                      <th>Actions</th>
                      <th>#</th>
                      <th style={{ maxWidth: "100px" }}>Username</th>
                      <th style={{ maxWidth: "100px" }}>First Name</th>
                      <th style={{ maxWidth: "100px" }}>Last Name</th>
                      <th style={{ maxWidth: "100px" }}>Mobile</th>
                      <th style={{ maxWidth: "200px" }}>Status</th>
                      <th style={{ maxWidth: "130px" }}>Entrydate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr>
                        <td colSpan={8}>Loading...</td>
                      </tr>
                    )}

                    {!loading && drivers.length === 0 && (
                      <tr>
                        <td colSpan={8}>No drivers available</td>
                      </tr>
                    )}

                    {!loading &&
                      drivers.map((d, idx) => (
                        <tr key={d.customerId || idx}>
                          <td className="table-action-btn-vendor">
                            <Dropdown onClick={(e) => e.stopPropagation()}>
                              <Dropdown.Toggle
                                variant="success"
                                id="dropdown-basic"
                                className="Dropdown-table"
                              >
                                <MoreVertIcon
                                  style={{
                                    color: "#f7f3f3ff",
                                    fontSize: "20px",
                                  }}
                                />
                              </Dropdown.Toggle>
                              <Dropdown.Menu className="Dropdown-table-item border-none">
                                <Dropdown.Item
                                  as={Link}
                                  to={`/vendor/dashboard/driverapprovaldetails?driverId=${d.customerId}&page=${currentPage}&size=${pageSize}`}
                                  className="Dropdown-table-list Dropdown-table-border-bottom border-none"
                                >
                                  View Details
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          </td>
                          <td>{idx + 1 + currentPage * pageSize}</td>
                          <td style={{ maxWidth: "100px" }}>{d.username}</td>
                          <td style={{ maxWidth: "100px" }}>{d.firstname}</td>
                          <td style={{ maxWidth: "100px" }}>{d.lastname}</td>
                          <td style={{ maxWidth: "100px" }}>{d.phone}</td>
                          <td style={{ maxWidth: "200px" }}>
                            <span
                              className="badge rounded-pill me-2"
                              style={{
                                backgroundColor:
                                  d.approvalStatus === "APPROVED"
                                    ? "#2DC24E"
                                    : "#ffc107",
                              }}
                            >
                              {d.approvalStatus === "APPROVED"
                                ? "Approved"
                                : "Pending"}
                            </span>
                          </td>
                          <td>{d.createdAt ? formatDate(d.createdAt) : "-"}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>

              <div className="text-end">
                <div className="flex items-center gap-2 justify-content-end">
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(parseInt(e.target.value, 10));
                      setCurrentPage(0);
                    }}
                    className="px-2 py-1 border rounded mx-1"
                  >
                    {[5, 10, 20, 25, 50].map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>

                  <button
                    className="px-3 py-1 border rounded disabled:opacity-50 mx-1 text-white"
                    disabled={currentPage === 0}
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 0))}
                  >
                    Previous
                  </button>

                  {Array.from({ length: Math.max(1, totalPages) }).map(
                    (_, page) => (
                      <button
                        key={page}
                        className={`px-3 py-1 border rounded mx-1 text-white ${
                          page === currentPage ? "current-page" : ""
                        }`}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page + 1}
                      </button>
                    )
                  )}

                  <button
                    className="px-3 py-1 border rounded disabled:opacity-50 mx-1 text-white"
                    disabled={currentPage >= totalPages - 1}
                    onClick={() =>
                      setCurrentPage((p) =>
                        Math.min(p + 1, Math.max(0, totalPages - 1))
                      )
                    }
                  >
                    Next
                  </button>
                </div>
                <div className="mt-2">{totalRecords} records</div>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default VendorDriverApproval;
